import * as lamejs from "@breezystack/lamejs";

/**
 * Convert WebM to MP3 using lamejs
 * @param webmBlob - WebM audio file
 * @param bitrate - Bitrate for MP3 (default 96 kbps, optimal for voice)
 * @returns Promise with MP3 Blob
 */
export async function convertWebmToMp3(
  webmBlob: Blob,
  bitrate: number = 96
): Promise<Blob> {
  // Decode WebM to PCM through AudioContext
  const arrayBuffer = await webmBlob.arrayBuffer();
  const audioContext = new AudioContext();
  const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

  const sampleRate = audioBuffer.sampleRate;
  const numChannels = audioBuffer.numberOfChannels;

  // Get PCM data and convert to mono (if stereo - mix channels)
  let monoChannel: Float32Array;

  if (numChannels === 1) {
    // Already mono - use as is
    monoChannel = audioBuffer.getChannelData(0);
  } else {
    // Stereo - mix channels into mono (average)
    const leftChannel = audioBuffer.getChannelData(0);
    const rightChannel = audioBuffer.getChannelData(1);
    monoChannel = new Float32Array(leftChannel.length);
    for (let i = 0; i < leftChannel.length; i++) {
      monoChannel[i] = (leftChannel[i] + rightChannel[i]) / 2;
    }
  }

  // Convert Float32Array to Int16Array for lamejs
  // Float32 [-1, 1] -> Int16 [-32768, 32767]
  const int16Samples = new Int16Array(monoChannel.length);
  for (let i = 0; i < monoChannel.length; i++) {
    const s = Math.max(-1, Math.min(1, monoChannel[i]));
    int16Samples[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }

  // Create MP3 encoder in mono mode (1 channel)
  const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, bitrate);

  const mp3Data: Uint8Array[] = [];
  const sampleBlockSize = 1152; // Recommended block size for MP3

  // Encode PCM into MP3 blocks
  for (let i = 0; i < int16Samples.length; i += sampleBlockSize) {
    const sampleChunk = int16Samples.subarray(i, i + sampleBlockSize);
    const mp3buf = mp3encoder.encodeBuffer(sampleChunk);
    if (mp3buf && mp3buf.length > 0) {
      mp3Data.push(mp3buf);
    }
  }

  const mp3buf = mp3encoder.flush();
  if (mp3buf && mp3buf.length > 0) {
    mp3Data.push(mp3buf);
  }

  const blobParts: BlobPart[] = mp3Data.map((buf) => {
    const newBuffer = new ArrayBuffer(buf.byteLength);
    const newView = new Uint8Array(newBuffer);
    newView.set(buf);
    return newView;
  });

  const mp3Blob = new Blob(blobParts, { type: "audio/mpeg" });

  await audioContext.close();

  return mp3Blob;
}
